import type { ImplementedGhostId } from "../ghosts/GhostDefinitions";
import type { BaitId } from "../inventory/ItemDefinitions";

export const CAMPAIGN_ORDER:readonly ImplementedGhostId[]=["toyol","polong","pocong","penanggal","pontianak"];
export type EncounterState="REVEAL"|"COMBAT"|"CAPTURE_READY"|"CAPTURED"|"LEVEL_COMPLETE"|"NEXT_LEVEL"|"FINAL_VICTORY";

export const CAMPAIGN_BAIT:Readonly<Record<ImplementedGhostId,BaitId>>={toyol:"coins",polong:"candle",pocong:"trampoline",penanggal:"fake-blood",pontianak:"mirror"};
export const CAMPAIGN_CLUES:Readonly<Record<ImplementedGhostId,readonly string[]>>={
  toyol:["Bunyi ranting patah...","Ketawa seakan kanak-kanak...","Kelibat kecil di celah pokok..."],
  polong:["Cahaya kecil berkelip...","Bau hangit semakin kuat...","Api bergerak sendiri..."],
  pocong:["Bunyi sesuatu melompat...","Kelibat putih di sebalik pokok..."],
  penanggal:["Bayang terbang di atas pokok...","Bau hanyir semakin kuat..."],
  pontianak:["Bau bunga dari arah hutan...","Kedengaran suara perempuan ketawa..."],
};

export interface EncounterDirectorEvents{
  onClue:(text:string)=>void;
  onLevelChanged:(level:number,ghost:ImplementedGhostId)=>void;
  onReveal:(ghost:ImplementedGhostId)=>void;
  onWrongBait:(bait:BaitId,expected:BaitId)=>void;
  onLevelComplete:(ghost:ImplementedGhostId,next?:ImplementedGhostId)=>void;
  onFinalVictory:()=>void;
}

export class EncounterDirector{
  private index:number;
  private currentState:EncounterState="REVEAL";
  private timer=0;
  private clueIndex=0;
  private readonly completedSet:Set<ImplementedGhostId>;
  constructor(private readonly events:EncounterDirectorEvents,initialLevel=1,completed:readonly ImplementedGhostId[]=[],private readonly hintIntervalMultiplier=1){
    this.completedSet=new Set(completed);this.index=Math.max(0,Math.min(CAMPAIGN_ORDER.length-1,Math.floor(initialLevel)-1));
    while(this.index<CAMPAIGN_ORDER.length-1&&this.completedSet.has(CAMPAIGN_ORDER[this.index]!))this.index++;
  }
  get state():EncounterState{return this.currentState;}
  get level():number{return this.index+1;}
  get currentGhost():ImplementedGhostId{return CAMPAIGN_ORDER[this.index]!;}
  get completedGhosts():readonly ImplementedGhostId[]{return CAMPAIGN_ORDER.filter((id)=>this.completedSet.has(id));}
  start():void{if(this.completedSet.size>=CAMPAIGN_ORDER.length){this.setState("FINAL_VICTORY");this.events.onFinalVictory();return;}this.beginLevel();}
  update(deltaSeconds:number):void{
    this.timer+=Math.max(0,deltaSeconds);
    if(this.currentState==="COMBAT"&&this.timer>=2.35*this.hintIntervalMultiplier){this.timer=0;const clues=CAMPAIGN_CLUES[this.currentGhost];if(this.clueIndex<Math.min(2,clues.length))this.events.onClue(clues[this.clueIndex++]!);}
  }
  handleBaitPlaced(bait:BaitId):boolean{
    if(!["COMBAT","CAPTURE_READY"].includes(this.currentState))return false;
    const expected=CAMPAIGN_BAIT[this.currentGhost];if(bait!==expected){this.events.onWrongBait(bait,expected);return false;}
    return true;
  }
  markCaptureReady(ghost:ImplementedGhostId):void{if(ghost===this.currentGhost&&this.currentState==="COMBAT")this.setState("CAPTURE_READY");}
  markCaptured(ghost:ImplementedGhostId):boolean{
    if(ghost!==this.currentGhost||this.completedSet.has(ghost))return false;
    this.completedSet.add(ghost);this.setState("CAPTURED");
    if(this.completedSet.size>=CAMPAIGN_ORDER.length){this.setState("FINAL_VICTORY");this.events.onLevelComplete(ghost);this.events.onFinalVictory();}
    else{const next=CAMPAIGN_ORDER[this.index+1]!;this.setState("LEVEL_COMPLETE");this.events.onLevelComplete(ghost,next);this.setState("NEXT_LEVEL");this.index++;this.beginLevel();}
    return true;
  }
  retryCurrentLevel():void{this.beginLevel();}
  private beginLevel():void{this.clueIndex=0;this.timer=0;this.setState("REVEAL");this.events.onLevelChanged(this.level,this.currentGhost);this.events.onClue(CAMPAIGN_CLUES[this.currentGhost][this.clueIndex++]!);this.events.onReveal(this.currentGhost);this.setState("COMBAT");}
  private setState(state:EncounterState):void{this.currentState=state;this.timer=0;}
}
